import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'

import resourceSettings from 'settings/resources'

import ResourceIconTitle from 'components/resource/ResourceIconTitle'
import SectionSelect from 'components/resource/SectionSelect'
import PriceSummary from 'components/resource/PriceSummary'

import SimpleTable from 'components/table/SimpleTable'
import SimpleTableActions from 'components/table/SimpleTableActions'

import FolderPathTitle from 'components/resource/FolderPathTitle'

import icons from 'settings/icons'
import selectors from 'store/selectors'
import routerActions from 'store/modules/router'
import networkActions from 'store/modules/network'
import resourceActions from 'store/modules/resource'

const AddIcon = icons.add
const ForwardIcon = icons.forward

const {
  getSections,
} = resourceSettings

const useStyles = makeStyles(theme => ({
  dialogPaperRoot: {
    height: '100%',
  },
  button: {
    marginRight: theme.spacing(2),
  },
  dialogTitle: {
    paddingBottom: '0px',
  },
  searchTextField: {
    width: '100%',
    marginTop: '0px',
    marginBottom: '0px',
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  linkContainer: {
    cursor: 'pointer',
  },
  linkTitle: {
    textDecoration: 'underline',
    color: theme.palette.primary.main,
  },
  priceCell: {
    width: '200px',
    minWidth: '200px',
    maxWidth: '200px',
  },
}))

const Finder = ({
  open,
  title,
  includeFilter,
  excludeFilter,
  onSave,
  onCancel,
}) => {

  const classes = useStyles()
  const dispatch = useDispatch()

  const currency = useSelector(selectors.project.currency)
  const items = useSelector(selectors.resource.finderListCollection.list)
  const search = useSelector(selectors.resource.finderSearch)
  const location = useSelector(selectors.resource.finderLocation)
  const folderPath = useSelector(selectors.resource.finderFolderPath)

  const {
    section,
    parentid,
  } = location

  const resetSearch = () => resourceActions.setFinderSearch('')

  const loadList = (payload) => dispatch(resourceActions.loadList(payload))
  const onUpdateSearch = (payload) => dispatch(resourceActions.updateSearch(payload))
  const setFinderLocation = (payload) => dispatch(resourceActions.setFinderLocation(payload))

  const loadSection = (section) => {
    resetSearch()
    setFinderLocation({
      section,
      parentid: 'root',
    })
  }

  const onOpen = () => {
    const useSections = getSections({
      includeFilter,
      excludeFilter,
    })
    if(!useSections.find(s => s.id == section)) {
      const openSection = useSections[0]
      loadSection(openSection.id)
    }
  }

  const loadParent = (parentid) => {
    resetSearch()
    setFinderLocation({
      section,
      parentid,
    })
  }

  const onClose = () => {
   onCancel()
  }

  const getList = () => {

    const folders = items.filter(item => item.type == 'folder')
    const nonFolders = items.filter(item => item.type != 'folder')

    const sortedItems = folders.concat(nonFolders)

    const fields =[{
      title: 'Name',
      name: 'name',
    }, {
      title: 'Price',
      name: 'price',
      className: classes.priceCell,
    }]

    const data = sortedItems.map((resource, index) => {
      return {
        id: resource.id,
        type: resource.type,
        rawName: resource.name,
        resource: resource,
        name: (
          <ResourceIconTitle
            resource={ resource }
            className={ classes.linkContainer }
            textClassname={ classes.linkTitle }
            onClick={ 
              resource.type == 'folder' ?
                () => loadParent(resource.id) :
                () => onSave(resource)
            }
          />
        ),
        price: resource.type == 'folder' ? null : (
          <PriceSummary
            currency={ currency }
            resource={ resource }
            amount={ 1 }
            unit={ 'day' }
          />
        )
      }
    })

    const getActions = (item) => {
      let actions = []

      if(item.type == 'folder') {
        actions.push({
          title: 'View',
          icon: ForwardIcon,
          handler: (item) => loadParent(item.id),
        })
      }
      else {
        actions.push({
          title: 'Add',
          icon: AddIcon,
          handler: (item) => onSave(item.resource),
        })
      }

      return (
        <SimpleTableActions
          item={ item }
          actions={ actions }
        />
      )
    }

    return (
      <SimpleTable
        data={ data }
        fields={ fields }
        getActions={ getActions }
      />
    )
  }

  useEffect(() => {
    if(open) {
      onOpen()
    }
  }, [
    open,
  ])

  useEffect(() => {
    if(!open) return
    if(!section) return
    loadList({
      section,
      parentid,
      finder: true,
    })
  }, [
    open,
    section,
    parentid,
  ])
  
  return (
    <Dialog
      open={ open }
      onClose={ onCancel }
      fullWidth
      maxWidth="md"
      classes={{
        paper: classes.dialogPaperRoot
      }}
    >
      <DialogTitle className={ classes.dialogTitle }>
        <FolderPathTitle
          title={ title }
          section={ section }
          folderPath={ folderPath }
          search={ search }
          onView={ ({parentid}) => loadParent(parentid) }
        />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 6 }>
            <TextField
              className={ classes.searchTextField }
              id="search"
              label="Search"
              value={ search }
              onChange={ (e) => onUpdateSearch({
                section,
                parentid,
                search: e.target.value,
                finder: true,
              }) }
              margin="normal"
            />
          </Grid>
          <Grid item xs={ 6 } className={ classes.sectionContainer }>
            <SectionSelect
              value={ section }
              includeFilter={ includeFilter }
              excludeFilter={ excludeFilter }
              onChange={ (e) => {
                loadSection(e.target.value)
              }}
            />
          </Grid>
          <Grid item xs={ 12 }>
            { getList() }
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          className={ classes.button }
          type="button"
          variant="contained"
          onClick={ onCancel }
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )

}

export default Finder

